import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, PencilIcon, TrashIcon, BuildingStorefrontIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Channels = () => {
  const [channels, setChannels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    base_price_type: 'commercial',
    first_level_discount: '',
    second_level_discount: '',
    third_level_discount: '',
    agent_cost: '',
    advance_payment: '',
    amortization: '',
    shipping: '',
    year_end_bonus: '',
    gift_cost: '',
    notes: ''
  });

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/channels`);
      setChannels(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei canali:', error);
      toast.error('Errore nel caricamento dei canali di vendita');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        first_level_discount: parseFloat(formData.first_level_discount) || 0,
        second_level_discount: parseFloat(formData.second_level_discount) || 0,
        third_level_discount: parseFloat(formData.third_level_discount) || 0,
        agent_cost: parseFloat(formData.agent_cost) || 0,
        advance_payment: parseFloat(formData.advance_payment) || 0,
        amortization: parseFloat(formData.amortization) || 0,
        shipping: parseFloat(formData.shipping) || 0,
        year_end_bonus: parseFloat(formData.year_end_bonus) || 0,
        gift_cost: parseFloat(formData.gift_cost) || 0
      };

      if (editingChannel) {
        await axios.put(`${API}/channels/${editingChannel.id}`, payload);
        toast.success('Canale di vendita aggiornato con successo');
      } else {
        await axios.post(`${API}/channels`, payload);
        toast.success('Canale di vendita creato con successo');
      }

      await loadChannels();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio del canale di vendita');
    }
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      code: channel.code,
      base_price_type: channel.base_price_type || 'commercial',
      first_level_discount: channel.first_level_discount?.toString() || '0',
      second_level_discount: channel.second_level_discount?.toString() || '0',
      third_level_discount: channel.third_level_discount?.toString() || '0',
      agent_cost: channel.agent_cost?.toString() || '0',
      advance_payment: channel.advance_payment?.toString() || '0',
      amortization: channel.amortization?.toString() || '0',
      shipping: channel.shipping?.toString() || '0',
      year_end_bonus: channel.year_end_bonus?.toString() || '0',
      gift_cost: channel.gift_cost?.toString() || '0',
      notes: channel.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo canale di vendita?')) {
      return;
    }

    try {
      await axios.delete(`${API}/channels/${id}`);
      toast.success('Canale di vendita eliminato con successo');
      await loadChannels();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del canale di vendita');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/channels/${id}/duplicate`);
      toast.success('Canale duplicato con successo');
      await loadChannels();
    } catch (error) {
      console.error('Errore nella duplicazione:', error);
      toast.error('Errore nella duplicazione del canale');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      base_price_type: 'commercial',
      first_level_discount: '',
      second_level_discount: '',
      third_level_discount: '',
      agent_cost: '',
      advance_payment: '',
      amortization: '',
      shipping: '',
      year_end_bonus: '',
      gift_cost: '',
      notes: ''
    });
    setEditingChannel(null);
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="channels-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Canali di Vendita</h1>
          <p className="text-gray-600">Gestisci i canali di vendita con sconti e costi specifici</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-channel-btn">
              <PlusIcon className="w-4 h-4 mr-2" />
              Nuovo Canale
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {editingChannel ? 'Modifica Canale' : 'Nuovo Canale di Vendita'}
              </DialogTitle>
              <DialogDescription>
                Inserisci i dettagli del canale di vendita con sconti a livelli e costi specifici.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Canale *</Label>
                  <Input
                    id="name"
                    data-testid="channel-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Es. Distributori Nazionali"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Codice *</Label>
                  <Input
                    id="code"
                    data-testid="channel-code-input"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Es. DIST-NAZ"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="base_price_type">Prezzo di Partenza *</Label>
                <select
                  id="base_price_type"
                  data-testid="channel-price-type-select"
                  value={formData.base_price_type}
                  onChange={(e) => setFormData({...formData, base_price_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="commercial">Listino Commerciale (senza accisa)</option>
                  <option value="public">Listino Pubblico (con IVA e accisa)</option>
                  <option value="average">Vendita Media</option>
                </select>
                <p className="text-xs text-gray-500">
                  Scegli quale prezzo usare come base per calcolare i margini su questo canale
                </p>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Sconti a Livelli (%)</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_level_discount">Sconto 1° Livello</Label>
                    <Input
                      id="first_level_discount"
                      data-testid="channel-discount1-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.first_level_discount}
                      onChange={(e) => setFormData({...formData, first_level_discount: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="second_level_discount">Sconto 2° Livello</Label>
                    <Input
                      id="second_level_discount"
                      data-testid="channel-discount2-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.second_level_discount}
                      onChange={(e) => setFormData({...formData, second_level_discount: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="third_level_discount">Sconto 3° Livello</Label>
                    <Input
                      id="third_level_discount"
                      data-testid="channel-discount3-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.third_level_discount}
                      onChange={(e) => setFormData({...formData, third_level_discount: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Costi Aggiuntivi (%)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent_cost">Costo Agente (%)</Label>
                    <Input
                      id="agent_cost"
                      data-testid="channel-agent-cost-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.agent_cost}
                      onChange={(e) => setFormData({...formData, agent_cost: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="advance_payment">Sconto Pagamento Anticipato (%)</Label>
                    <Input
                      id="advance_payment"
                      data-testid="channel-advance-payment-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.advance_payment}
                      onChange={(e) => setFormData({...formData, advance_payment: e.target.value})}
                      placeholder="0.0"
                    />
                    <p className="text-xs text-gray-500">Sconto aggiuntivo applicato insieme agli sconti 1,2,3</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amortization">Ammortamenti (%)</Label>
                    <Input
                      id="amortization"
                      data-testid="channel-amortization-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.amortization}
                      onChange={(e) => setFormData({...formData, amortization: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping">Spedizione (%)</Label>
                    <Input
                      id="shipping"
                      data-testid="channel-shipping-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.shipping}
                      onChange={(e) => setFormData({...formData, shipping: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="year_end_bonus">Premio Fine Anno - PFA (%)</Label>
                    <Input
                      id="year_end_bonus"
                      data-testid="channel-year-end-bonus-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.year_end_bonus}
                      onChange={(e) => setFormData({...formData, year_end_bonus: e.target.value})}
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gift_cost">Omaggio (€)</Label>
                    <Input
                      id="gift_cost"
                      data-testid="channel-gift-cost-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.gift_cost}
                      onChange={(e) => setFormData({...formData, gift_cost: e.target.value})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500">Costo fisso in euro per bottiglia</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  data-testid="channel-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive sul canale di vendita"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="cancel-channel-btn"
                >
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-channel-btn">
                  {editingChannel ? 'Aggiorna' : 'Salva'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="🔍 Cerca per nome o codice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {channels.filter(ch => 
                ch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ch.code.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} risultati trovati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Channels List */}
      {channels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BuildingStorefrontIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun canale di vendita</h3>
            <p className="text-gray-500 text-center mb-6">
              I canali di vendita permettono di definire condizioni specifiche per diversi mercati.
              <br />
              Ogni canale può avere sconti a più livelli e costi aggiuntivi.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-6 max-w-md">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Esempi di canali:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Distributori Nazionali (sconti alti, costi trasporto)</li>
                <li>• Vendita Diretta (margini pieni, no costi agente)</li>
                <li>• Export (sconti medi, costi logistici)</li>
                <li>• E-commerce (sconti bassi, costi spedizione)</li>
              </ul>
            </div>
            <Button onClick={() => setIsModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Crea primo canale
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {channels
            .filter(ch => 
              ch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              ch.code.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((channel) => (
            <Card key={channel.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <CardTitle className="text-lg">{channel.name}</CardTitle>
                      <Badge variant="outline">{channel.code}</Badge>
                      {channel.base_price_type && (
                        <Badge variant="secondary" className="text-xs">
                          {channel.base_price_type === 'commercial' && '💼 Listino Comm.'}
                          {channel.base_price_type === 'public' && '🏷️ Listino Pubb.'}
                          {channel.base_price_type === 'average' && '📊 Vendita Media'}
                        </Badge>
                      )}
                    </div>
                    <CardDescription>
                      Sconti: {formatPercentage(channel.first_level_discount)} / {formatPercentage(channel.second_level_discount)} / {formatPercentage(channel.third_level_discount)}
                      {(channel.agent_cost > 0 || channel.advance_payment > 0 || channel.amortization > 0 || channel.shipping > 0 || channel.year_end_bonus > 0 || channel.gift_cost > 0) && (
                        <>
                          <br />
                          Costi %: {channel.agent_cost > 0 && `Agente ${formatPercentage(channel.agent_cost)}`}
                          {channel.advance_payment > 0 && ` • Pag.Ant. ${formatPercentage(channel.advance_payment)}`}
                          {channel.amortization > 0 && ` • Ammort. ${formatPercentage(channel.amortization)}`}
                          {channel.shipping > 0 && ` • Sped. ${formatPercentage(channel.shipping)}`}
                          {channel.year_end_bonus > 0 && ` • PFA ${formatPercentage(channel.year_end_bonus)}`}
                          {channel.gift_cost > 0 && ` • Omaggio ${formatCurrency(channel.gift_cost)}`}
                        </>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(channel)}
                      data-testid={`edit-channel-${channel.id}`}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(channel.id)}
                      data-testid={`duplicate-channel-${channel.id}`}
                      title="Duplica canale"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(channel.id)}
                      data-testid={`delete-channel-${channel.id}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {channel.notes && (
                <CardContent>
                  <p className="text-sm text-gray-600">{channel.notes}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Channels;